import React, { useCallback, useMemo } from "react";
import * as Select from "@radix-ui/react-select";
import { AHIcon, AHText, Box, useSpaceSize } from "arkh";
import styles from "./WorkspaceSelector.module.css";
import { SelectorContent } from "../OrgAdmin/OrgUsers/Selector";
import { graphql, useFragment } from "react-relay";
import { WorkspaceSelectorContent_workspaces$key } from "./__generated__/WorkspaceSelectorContent_workspaces.graphql";

type A<B> = B & {
  children?: B[];
};

export function listToTree<
  T extends { readonly parentId: string | null; readonly id: string },
>(_list: readonly T[]): readonly A<T>[] {
  const map: Record<string, number> = {};
  let node: T;

  const list = _list.map((v, i) => ({ ...v, children: [] as T[] }));
  const roots = [];
  for (let i = 0; i < list.length; i += 1) {
    map[list[i].id] = i;
    list[i].children = [];
  }

  for (let i = 0; i < list.length; i += 1) {
    node = list[i];
    if (node.parentId) {
      list[map[node.parentId]]?.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export function WorkspaceSelectorContent(props: {
  workspaces: WorkspaceSelectorContent_workspaces$key;
  selectedId: string | undefined;
  disabledIds?: string[];
}) {
  const workspaces = useFragment(
    graphql`
      fragment WorkspaceSelectorContent_workspaces on Workspace
      @relay(plural: true) {
        id
        name
        parentId
      }
    `,
    props.workspaces
  );
  const tree = useMemo(() => listToTree(workspaces), [workspaces]);

  const renderItem = useCallback(
    (w: (typeof tree)[0]) => {
      return (
        <WorkspaceSelectorListItem
          key={w.id}
          data={w}
          selectedId={props.selectedId}
          disabledIds={props.disabledIds}
        />
      );
    },
    [props.disabledIds, props.selectedId]
  );

  return (
    <SelectorContent>
      {tree
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(renderItem)}
    </SelectorContent>
  );
}

type WorkspaceSelectorListItemData = {
  id: string;
  name: string;
  children?: readonly WorkspaceSelectorListItemData[] | undefined;
};

function WorkspaceSelectorListItem(props: {
  data: WorkspaceSelectorListItemData;
  selectedId: string | undefined;
  disabledIds?: string[];
}) {
  const horizontal = useSpaceSize("small");
  const disabled = props.disabledIds?.includes(props.data.id);
  return (
    <Box>
      <Box spaceHorizontal="small">
        <Select.Item
          disabled={disabled}
          value={props.data.id}
          className={styles.SelectItem}
          style={{
            fontSize: 13,
            lineHeight: 1,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            height: 40,
            paddingLeft: horizontal,
            paddingRight: horizontal,
            position: "relative",
            userSelect: "none",
          }}
        >
          <Box style={{ flex: 1 }}>
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                flex: 1,
              }}
            >
              <Select.ItemText>
                <AHText
                  type="subtitle"
                  color={disabled ? "text-secondary" : "text-primary"}
                  spaceRight="large"
                >
                  {props.data.name}
                </AHText>
              </Select.ItemText>
            </Box>
          </Box>
          <Select.ItemIndicator />
          {props.selectedId === props.data.id ? (
            <AHIcon type="check" color="primary" />
          ) : null}
        </Select.Item>
      </Box>

      <Box spaceLeft="medium">
        <Box style={{ borderLeftWidth: 2 }} borderColor="border-primary">
          {props.data.children
            ?.slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((w) => (
              <WorkspaceSelectorListItem
                key={w.id}
                data={w}
                selectedId={props.selectedId}
                disabledIds={props.disabledIds}
              />
            ))}
        </Box>
      </Box>
    </Box>
  );
}
